import { InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { Backdrop, CircularProgress } from '@mui/material'
import { protectedResources } from '@src/authConfig'
import {
  CACHED_USER_TOKEN,
  JWT_LOCAL_STORAGE_KEY,
  loadUserFromJwtString,
  UserContext,
} from '@src/contexts/user-context'
import { signIn } from '@src/data/api/user/user-api'
import type { User } from '@src/data/types'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useContext, useEffect, useMemo, useState, type JSX } from 'react'

interface Props {
  children: JSX.Element
}

const CACHED_USER = loadUserFromJwtString(CACHED_USER_TOKEN)

const PageLoader = () => (
  <Backdrop open invisible={false} sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}>
    <CircularProgress />
  </Backdrop>
)

const UserProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | null>(CACHED_USER)
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const { selectedCompany, setSelectedCompany } = usePreferences()

  useEffect(() => {
    if (user && !selectedCompany && user.companyIds[0]) {
      setSelectedCompany(user.companyIds[0])
    }
  }, [user, selectedCompany, setSelectedCompany])

  useEffect(() => {
    if (inProgress === InteractionStatus.None && isAuthenticated && user === null) {
      const activeAccount = accounts[0]
      instance.setActiveAccount(activeAccount)
      instance
        .acquireTokenSilent({ account: activeAccount, scopes: protectedResources.getTokenAzure.scopes.read })
        .then((response) => {
          return signIn({ jwt: response.accessToken }).then((data) => {
            const parsedUser = loadUserFromJwtString(data?.jwt)
            if (!parsedUser) {
              return instance.loginRedirect({
                account: activeAccount,
                scopes: protectedResources.getTokenAzure.scopes.read,
              })
            }
            setUser(parsedUser)
            localStorage.setItem(JWT_LOCAL_STORAGE_KEY, data.jwt)
            return null
          })
        })
        .catch(() => {
          reportErrorToConsole(new Error('Failed to acquire token silently. Redirecting to login.'))
          instance.loginRedirect({ scopes: protectedResources.getTokenAzure.scopes.read }).catch(reportErrorToConsole)
        })
    }
  }, [inProgress, isAuthenticated, user, accounts, instance])

  const ctx = useMemo(() => (user ? { user } : null), [user])

  if (inProgress !== InteractionStatus.None || (isAuthenticated && user === null)) {
    return <PageLoader />
  }

  return <UserContext.Provider value={ctx}>{children}</UserContext.Provider>
}

export const useUser = (): User => {
  const ctx = useContext(UserContext)
  if (!ctx?.user) {
    throw new Error('useUser must be used within UserProvider')
  }
  return ctx.user
}

/**
 * Returns the user if authenticated, or null if not.
 * Safe to use in components that may render before authentication.
 */
export const useOptionalUser = (): User | null => {
  const ctx = useContext(UserContext)
  return ctx?.user ?? null
}

export const useHasRole = (role: string): boolean => {
  const user = useOptionalUser()
  return user?.roles.includes(role) ?? false
}

export const useIsIcebergGrDevs = (): boolean => {
  return useHasRole('IcebergGrDevs')
}

export const useCanConsultUnreadMessages = (): boolean => {
  const user = useOptionalUser()
  return user?.rights.canConsultUnreadMessages ?? false
}

export default UserProvider
